/**
 * useMonetizationTrigger — WHEN the popup appears, kept apart from WHAT it says.
 *
 * This is glowtest_FE's `useHomepageTriggers` idea, reduced to the one trigger raisy actually needs
 * at this stage: **the wallet is empty**. That is not a growth-hack trigger, it is a service
 * message — the user's outreach has paused and he needs to know why.
 *
 * Rules, each of them deliberate:
 *   - it reads the balance ONCE per mount, not on a poll — an unread balance must never look like
 *     a spent one;
 *   - `undefined` means "not read yet" and opens NOTHING. Only a measured 0 opens the wall;
 *   - it opens at most once per session, so a user who dismisses it is not asked again.
 *
 * The 402 path is the other half: when a send is refused for lack of credits, the caller can invoke
 * `useMonetization().open("out_of_credits")` directly — same dialog, same numbers.
 */
import { useEffect, useRef } from "react";
import { getMySubscription } from "@/services/api/monetizationApi";
import type { MonetizationReason } from "./MonetizationDialog";

interface Args {
  credits: number | undefined;
  shownThisSession: boolean;
  open: (reason?: MonetizationReason) => void;
  setCredits: (n: number | undefined) => void;
  /** The plan the payer is on, so surfaces can mark it and price at his own rate (port 2026-09-21). */
  setTier: (t: string | undefined) => void;
  /** The rate the backend will debit for one reachout, from the same response. */
  setPerSendCost: (n: number | undefined) => void;
}

/** Read the balance once, in the background, and never turn "unknown" into "zero". */
export function useMonetizationTrigger({
  credits,
  shownThisSession,
  open,
  setCredits,
  setTier,
  setPerSendCost,
}: Args) {
  const read = useRef(false);

  useEffect(() => {
    if (read.current) return;
    read.current = true;
    let cancelled = false;
    (async () => {
      try {
        // ONE READ FOR BOTH FACTS, and it is the route that actually carries them (PO report
        // 2026-09-21: *"the header which is the center item is not working … its empty and doesnt show
        // me any number"*).
        //
        // It used to call `/credits/get_credit_balance` and read `data.credits ?? data.balance`.
        // MEASURED against DEV with a real token, that route answers:
        //   data = {entity_id, entitykey, credit_balance: 0.0, credit_grants_received: 0.0}
        // so the key is `credit_balance`, NEITHER of the two names read — which is why the wallet sat
        // on its "reading…" placeholder forever instead of ever showing a figure. (glowtest's own
        // backend returns `current_credits`, which is where the ported names came from: the port
        // copied the CALL but not this repo's key.)
        //
        // The same payload showed there is NO `tier` in it either, yet line below read `data.tier` to
        // set the plan — so the dialog never learned which plan was paid for, and the send button's
        // cost preview never had a rate to price with. `/subscription/my_subscription` returns the
        // balance object AND the tier AND the per-reachout rate of that tier, so one call now carries
        // everything both surfaces need.
        const res = await getMySubscription();
        if (cancelled) return;
        const data = res?.data;
        const n = data?.credit_balance;
        // Only a MEASURED number is stored. An unreadable balance stays `undefined`.
        if (typeof n === "number") setCredits(n);
        // The plan, from the same response. Two surfaces need it: the dialog marks the plan already
        // paid for (so a Growth customer cannot buy Growth twice by accident), and the cost preview
        // prices an action at THIS payer's own rate — the only correct rate, because a free account
        // pays the entry 196 where a paid tier pays 196 / 172 / 164, and only the server knows which.
        // Undefined stays undefined: an unknown plan shows no price rather than a price computed on
        // somebody else's tier.
        const t = data?.tier;
        if (typeof t === "string" && t) setTier(t);
        // AND THE RATE, from the server rather than from the ladder. A free payer is on no plan CARD,
        // so a preview that looked the rate up from `PLANS` rendered NOTHING for exactly the person
        // who most needs it (five free sends and no idea what a batch costs). `per_send_credit_cost`
        // is the number the gate will debit, so showing it is not a guess — it is the price.
        const rate = data?.per_send_credit_cost;
        if (typeof rate === "number") setPerSendCost(rate);
      } catch {
        // A failed read is not an empty wallet. Say nothing, show nothing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setCredits, setTier, setPerSendCost]);

  // The wall opens on a MEASURED zero, once per session.
  useEffect(() => {
    if (shownThisSession) return;
    if (credits === 0) open("out_of_credits");
  }, [credits, shownThisSession, open]);
}
